<template>
  <div class="mx-auto px-4 pb-12 sm:px-4 lg:px-8">
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-6 mb-6">
      <BaseWidget label="Total Aktif" :value="getMembers.meta.total_active"/>
      <BaseWidget label="Total Proses Kasir" :value="getMembers.meta.total_valid"/>
      <BaseWidget label="Total Draft" :value="getMembers.meta.total_draft"/>
    </div>
    <BaseCard>
      <div class="flex flex-col">
        <div class="grid gap-2 md:flex xl:justify-end relative">
          <base-button color="white" class="lg:hidden" @click="visibleFilter = !visibleFilter">Filter</base-button>
          <base-button :to="{ name: 'daftar-member.tambah' }">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            Tambah Mitra Usaha
          </base-button>
          <BaseDropdown id="export-mitra-usaha-dropdown" class="flex items-end">
            <template #toggle="{ toggle }">
              <base-button
                fullwidth
                :loading="loadingExport"
                :disabled="loadingExport"
                @click="toggle"
              >
                <p v-if="loadingExport">Sedang Diexport</p>
                <template v-else>
                  <Icon icon="heroicons:arrow-down-tray" class="h-4 w-4" />
                  Export
                </template>
              </base-button>
            </template>

            <template v-if="!loadingExport" #content>
              <div class="py-1">
                <button @click="onExport('pdf')" class="px-3 py-2 w-full text-left whitespace-nowrap text-sm text-gray-900 hover:bg-gray-50">Export PDF</button>
                <button @click="onExport('csv')" class="px-3 py-2 w-full text-left whitespace-nowrap text-sm text-gray-900 hover:bg-gray-50">Export CSV</button>
              </div>
            </template>
          </BaseDropdown>
          <div :class="[visibleFilter ? 'flex' : 'hidden', 'flex-col w-full gap-4 lg:flex lg:flex-row lg:items-end lg:w-auto lg:order-first']">
            <base-input label="Status" with-label>
              <base-select
                :shadow="false"
                :expand="true"
                :options="statusOptions"
                v-model="filter.status"
                v-on:change="handleFilter"
              />
            </base-input>
            <MitraPeriodMonthSelect
              v-model="filter.mitraPeriodId"
              v-on:change="handleFilter"
            />
            <base-input type="text" class="col-span-full" label="Cari" fullwidth :lgFullwidth="false" :shadow="false" with-label placeholder="Cari Kode" v-model="search" debounce @native-input="debounceSearch" />
          </div>
        </div>
        <div class="my-2 flex sm:-mx-6 lg:-mx-8">
          <div
            class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
          >
            <Datatable
              :total="getMembers.meta.page.total"
              :perPage="getMembers.meta.page.perPage"
              :currentPage="getMembers.meta.page.currentPage"
              :meta="getMembers.meta"
              @pagechanged="onPageChangeProducts"
              cursor
            >
              <template v-slot:thead>
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Nomor Mitra Usaha
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Nama Mitra Usaha
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      No. HP
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      No. Rek
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      No/Nama Upline
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      KTA
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Periode
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Status Proses
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Status
                    </th>
                  </tr>
                </thead>
              </template>
              <template v-slot:tbody>
                <div v-if="isLoading" class="flex items-center text-center">
                  <loading></loading>
                </div>
                <tbody v-if="getMembers.data.length">
                  <tr
                    v-for="(data, dataIdx) in getMembers.data"
                    :key="data.id"
                    :class="[dataIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50', checkIsEditable(data) ? 'cursor-pointer bg-white hover:bg-green-100' : '']"
                    @click="detail(data)"
                  >
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm font-bold text-gray-700"
                    >
                        {{ getOfficeIncluded(data).attributes.code ?? '-' }}
                        /
                        {{ getOfficeIncluded(data).attributes.temp_code ?? '-' }}
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm text-gray-500"
                    >
                      {{ getOfficeIncluded(data).attributes.name }}
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm text-gray-500"
                    >
                      <p v-if="!checkIsOwned(data)">-</p>
                      <template v-else>
                        <span v-if="getOfficeIncluded(data).attributes.phone">{{
                          getOfficeIncluded(data).attributes.phone
                        }}</span>
                        <span v-else>-</span>
                      </template>
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm text-gray-500"
                    >
                      <p v-if="!checkIsOwned(data)">-</p>
                      <template v-else>
                        <span
                          v-if="
                            getOfficeIncluded(data).attributes.bank_account_number
                          "
                          >{{
                            getOfficeIncluded(data).attributes.bank_account_number
                          }}</span
                        >
                        <span v-else>-</span>
                      </template>
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm text-gray-500"
                    >
                      {{
                        getOfficeIncluded(data).attributes.referred_by ?? '-'
                      }}
                      <div class="text-xs">
                        {{
                          getOfficeIncluded(data).attributes.referred_by_name ??
                          '-'
                        }}
                      </div>
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm text-gray-500"
                    >
                      <p v-if="!checkIsOwned(data)">-</p>
                      <template v-else>
                        {{
                          getOfficeIncluded(data).attributes.is_kta_need_delivery
                            ? 'Dikirim'
                            : 'Diambil'
                        }}
                      </template>
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-sm text-gray-500"
                    >
                      <p v-if="!checkIsOwned(data)">-</p>
                      <template v-else>
                        {{
                          data.relationships['mitra-period'].data
                            ? dayjs()
                                .month(getSingleIncluded(getMembers, data.relationships['mitra-period'].data.id).attributes.month - 1)
                                .year(getSingleIncluded(getMembers, data.relationships['mitra-period'].data.id).attributes.year)
                                .format('MMYY')
                            : '-'
                        }}
                      </template>
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500"
                    >
                      <p v-if="!checkIsOwned(data)">-</p>
                      <template v-else>
                        <span
                          s
                          v-if="
                            getOfficeIncluded(data).attributes.is_bonus_reported
                          "
                          class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"
                        >
                          Diproses
                        </span>
                        <span
                          v-else
                          class="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800"
                        >
                          Belum Diproses
                        </span>
                      </template>
                    </td>
                    <td
                      class="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500"
                    >
                      <p v-if="!checkIsOwned(data)">-</p>
                      <template v-else>
                        <span
                          v-if="
                            getOfficeIncluded(data).attributes.is_draft &&
                            !getOfficeIncluded(data).attributes.is_active
                          "
                          class="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800"
                          >Draft</span
                        >
                        <span
                          v-else-if="
                            !getOfficeIncluded(data).attributes.is_active &&
                            !getOfficeIncluded(data).attributes.is_draft
                          "
                          class="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800"
                          >Diproses Kasir</span
                        >
                        <span
                          v-else
                          class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"
                          >Aktif</span
                        >
                      </template>
                    </td>
                  </tr>
                </tbody>
                <tbody>
                  <tr class="bg-white">
                    <td
                      class="whitespace-nowrap px-6 py-4 text-left text-sm text-gray-500"
                    >
                      Total Mitra Usaha
                    </td>
                    <td
                      colspan="9"
                      class="whitespace-nowrap px-6 py-4 text-left text-sm text-gray-500"
                    >
                      {{ getMembers.meta.page.total }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </Datatable>
          </div>
        </div>
      </div>
    </BaseCard>
    <!-- MODAL VALIDASI KEMASAN-->
    <transition
      name="customFade"
      enter-active-class="fadeIn"
      leave-active-class="fadeOut"
    >
      <div
        v-if="openAddUser"
        style="animation-duration: 0.3s"
        class="fixed inset-0 z-10"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="mx-4 flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>
          <span
            class="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
            >&#8203;</span
          >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <form @submit.prevent="addUser">
              <div>
                <div class="mt-3 text-center sm:mt-5">
                  <h3
                    class="text-lg font-medium leading-6 text-gray-900"
                    id="modal-title"
                  >
                    Buat User
                  </h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      Lengkapi form dibawah ini untuk membuat user member
                    </p>
                  </div>
                </div>
                <div class="my-4 space-y-3">
                  <div class="field-inset-default w-full">
                    <label
                      for="email"
                      class="block text-xs font-bold text-gray-700"
                    >
                      Email
                    </label>
                    <input
                      v-model="email"
                      type="email"
                      name="email"
                      id="email"
                      class="block w-full border-0 p-0 text-gray-900 placeholder-gray-200 focus:ring-0 sm:text-sm"
                      placeholder="Masukkan email"
                    />
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-6 sm:flex sm:flex-row sm:gap-x-3">
                <button
                  type="submit"
                  class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                >
                  Ya
                </button>
                <button
                  @click="openAddUser = !openAddUser"
                  type="button"
                  class="focus:outline-none mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                >
                  Tutup
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="success"
        class="fixed inset-0 z-10 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
        id="modal_success"
      >
        <div
          class="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>
          <span
            class="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
            >&#8203;</span
          >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6 sm:align-middle"
          >
            <div>
              <div
                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
              >
                <svg
                  class="h-6 w-6 text-green-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <slot name="heading"> Data Berhasil disimpan </slot>
              </div>
            </div>
            <div class="mt-5 sm:mt-6">
              <div class="flex items-center gap-2">
                <base-button
                  color="yellow"
                  fullwidth
                  @click="closeModal"
                  type="button"
                >
                  Selesai
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="failed"
        class="fixed inset-0 z-10 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
        id="modal_error"
      >
        <div
          class="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>
          <span
            class="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
            >&#8203;</span
          >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6 sm:align-middle"
          >
            <div>
              <div
                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-red-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <slot name="heading">
                  {{ getError }}
                </slot>
              </div>
            </div>
            <div class="mt-5 sm:mt-6">
              <div class="flex items-center gap-2">
                <button
                  @click="closeModal"
                  class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:text-sm"
                >
                  Ulangi
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { debounce } from 'debounce';
import Datatable from '@/components/base/Datatable';
import MitraPeriodMonthSelect from '@/components/period/mitra-period/mitra-period-month-select.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseWidget from '../../../../../components/base/BaseWidget.vue';
import Loading from '@/components/Loading.vue';
import dayjs from 'dayjs';
import BaseDropdown from '../../../../../components/base/BaseDropdown.vue';
import { downloadFileUrl } from '../../../../../services/utils.service';
import { requestMixin } from '../../../../../mixins/request/request';

export default {
  mixins: [requestMixin],
  name: 'Users',
  components: {
    Datatable,
    BaseSelect,
    MitraPeriodMonthSelect,
    BaseInput,
    Loading,
    BaseWidget,
    BaseDropdown
  },
  data() {
    return {
      visibleFilter: false,
      search: null,
      disablePeriod: false,
      success: false,
      failed: false,
      openAddUser: false,
      roles_id: null,
      selectedMember: null,
      email: null,
      filter: {
        status: null,
        mitraPeriodId: null
      },
      loadingExport: false
    };
  },
  async created() {
    await this.loadCurrentPeriod();
    
    this.loadExportJobStatus()
    this.loadMembers();
    this.fetchRoles({
      search: 'User Member',
    }).then((response) => {
      if (response.data.data.length) {
        this.roles_id = response.data.data[0].id;
      }
    });
  },
  computed: {
    ...mapGetters({
      getMembers: 'simple_offices/getUplineSimpleOffices',
      isLoading: 'simple_offices/getLoading',
      me: 'auth/getUser',
      getCurrentPeriod: 'mitra_periods/getCurrentPeriod',
      getChannelName: 'auth/getChannelName'
    }),
    fetchMembersParams: function () {
      return {
        'page[limit]': 5,
        sort: '-id',
        include: 'office,mitra-period',
        'fields[simple-offices]': 'bonus_month,office,mitra-period',
        'fields[offices]':
          'code,temp_code,name,phone,bank_account_number,is_kta_need_delivery,is_draft,is_bonus_reported,is_active,email,registered_by,referred_by,referred_by_name,upline_histories',
        'filter[office_category_id]': 5,
        'filter[period_uuid]': this.filter.mitraPeriodId,
        'filter[search]': this.search,
        ...(this.filter.status
          ? this.filter.status === 'draft'
            ? { 'filter[is_draft]': true, 'filter[is_active]': false }
            : this.filter.status === 'process_cashier'
            ? { 'filter[is_draft]': false, 'filter[is_active]': false }
            : this.filter.status === 'active'
            ? { 'filter[is_active]': true }
            : {}
          : {}),
      };
    },
    statusOptions: function () {
      return [
        {
          key: 'all',
          value: null,
          label: 'Semua',
        },
        {
          key: 'draft',
          value: 'draft',
          label: 'Draft',
        },
        {
          key: 'process_cashier',
          value: 'process_cashier',
          label: 'Proses Kasir',
        },
        {
          key: 'active',
          value: 'active',
          label: 'Aktif',
        },
      ];
    }
  },
  methods: {
    dayjs,
    ...mapActions({
      fetchRoles: 'roles/fetchRoles',
      createUser: 'users/createUser',
      fetchCurrentPeriod: 'mitra_periods/fetchCurrentMitraPeriod',
      fetchMembersByUpline: 'simple_offices/fetchSimpleOfficesByUpline',
      createAlert: 'alert/createAlert'
    }),
    loadMembers(params) {
      this.fetchMembersByUpline({
        uplineId: this.me.office_id,
        params: {
          ...this.fetchMembersParams,
          ...params,
        },
      });
    },
    async loadExportJobStatus() {
      const [res, err] = await this.request(`/api/v1/offices/-actions/check-export-mitra-usaha`)

      if (!err) {
        if (res.status) {
          this.loadingExport = true
          this.startListenExportJob()
        }
      }
    },
    debounceSearch: debounce(function () {
      this.loadMembers();
    }, 300),
    onPageChangeProducts(page) {
      this.loadMembers(page);
    },
    async onExport(type) {
      this.loadingExport = true

      const [, err] = await this.request(`/api/v1/offices/-actions/export-mitra-usaha`, {
        method: 'post',
        data: {
          mitra_period_id: this.filter.mitraPeriodId,
          type: type
        }
      })

      if (err) {
        this.loadingExport = false

        this.createAlert({
          status: 'error',
          data: 'Gagal mengexport cashbill'
        })
      } else {
        this.startListenExportJob()
      }
    },
    detail(data) {
      if (this.checkIsEditable(data)) {
        this.$router.push({
          name: 'daftar-member.edit',
          params: {
            id: data.relationships.office.data.id
          }
        });
      }
    },
    closeModal() {
      this.success = this.failed = this.openAddUser = false;
      this.loadMembers();
    },
    checkIsEditable(member) {
      const memberIncluded = this.getOfficeIncluded(member)
      
      if (!memberIncluded.attributes.is_active) {
        return true
      }

      if (memberIncluded.attributes.is_bonus_reported) {
        return false
      }

      const periodIncluded = this.getSingleIncluded(this.getMembers, member.relationships['mitra-period'].data.id)

      return periodIncluded.id === this.getCurrentPeriod.data.id
    },
    checkIsOwned(member) {
      const uplines = this.getOfficeIncluded(member).attributes.upline_histories
      const register = uplines.find(upline => upline.type === 'registered_by')

      if (!register) {
        return false
      }

      return register.code === this.me.office_code
    },
    addUser() {
      const payload = {
        data: {
          type: 'users',
          attributes: {
            name: this.selectedMember.attributes.name,
            email: this.email,
          },
          relationships: {
            office: {
              data: {
                type: 'offices',
                id: this.selectedMember.id,
              },
            },
            roles: {
              data: [
                {
                  type: 'roles',
                  id: this.roles_id,
                },
              ],
            },
          },
        },
      };
      this.createUser(payload).then((response) => {
        response ? (this.success = true) : (this.failed = true);
      });
    },
    handleFilter: function () {
      const disablePeriod = ['process_cashier', 'draft'];
      if (disablePeriod.includes(this.filter.status)) {
        this.filter.disablePeriod = true;
      } else {
        this.filter.disablePeriod = false;
      }
      this.loadMembers();
    },
    async loadCurrentPeriod() {
      const res = await this.fetchCurrentPeriod({
        'fields[mitra-periods]': 'month,year,name',
      });

      this.filter.mitraPeriodId = res.data.data.id;
    },
    getOfficeIncluded(data) {
      return this.getSingleIncluded(
        this.getMembers,
        data.relationships.office.data.id
      );
    },
    startListenExportJob() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onProgressExportJob
      );
    },
    stopListenExportJob() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onProgressExportJob
        );
      }
    },
    onProgressExportJob(e) {
      if (e.type === 'App\\Notifications\\MitraUsahaExported') {
        this.loadingExport = false;

        this.createAlert({
          status: 'success',
          data: 'Berhasil mengexport saldo',
        });

        downloadFileUrl(e.file_url);

        this.stopListenExportJob()
      }
    },
    beforeRouteLeave(to, from, next) {
      this.stopListenExportJob();
      next();
    },
  },
};
</script>
